import {
  CreateDiscountType,
  DiscountTypesApi,
  ListDiscountTypes,
  ListDiscountTypesRequest,
  ListOrderedDiscountTypes,
  ResponseError,
  ShowDiscountType1,
  UpdateDiscountType,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import {
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query'
import { apiConfiguration, formatDateForApi } from 'utilities/TrybeApiClient'
import { SetOptional } from 'type-fest'

const api = new DiscountTypesApi(apiConfiguration)

export const useDiscountTypes = (
  params: SetOptional<ListDiscountTypesRequest, 'siteId'> = {},
  options = {}
) => {
  params.siteId = useCurrentSiteId()
  const [page] = usePageQueryParam()

  return useQuery<ListDiscountTypes, ResponseError>(
    ['discountTypes', { params }],
    () => api.listDiscountTypes({ ...params, page } as ListDiscountTypesRequest),
    options
  )
}

export const useInfiniteDiscountTypes = (params: any = {}, options = {}) => {
  params.siteId = useCurrentSiteId()

  return useInfiniteQuery(['discountTypesInfinite', { params }], fetchDiscountTypes, {
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage
    },
    staleTime: 10000,
    ...options,
  })
}

const fetchDiscountTypes = async ({ queryKey, pageParam: page = 1 }) => {
  const { params } = queryKey[1]

  const response = await api.listDiscountTypes({ page, ...params })

  return {
    data: response.data,
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

export const useDiscountType = (discountTypeId: string, options = {}) => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['discountTypes', { siteId, discountTypeId }],
    async () =>
      await api.getDiscountType({
        discountType: discountTypeId,
      }),
    options
  )
}

export const useCreateDiscountType = () => {
  const siteId = useCurrentSiteId()
  const queryClient = useQueryClient()

  return useMutation<ShowDiscountType1, ResponseError, CreateDiscountType>(
    (createDiscountType) =>
      api.createDiscountType({
        createDiscountType: {
          ...createDiscountType,
          site_id: siteId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['discountTypes'])
      },
    }
  )
}

export const useUpdateDiscountType = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowDiscountType1, ResponseError, [string, UpdateDiscountType]>(
    ([discountTypeId, updateDiscountType]) => {
      if (updateDiscountType.valid_from) {
        updateDiscountType.valid_from = formatDateForApi(updateDiscountType.valid_from)
      }

      if (updateDiscountType.valid_to) {
        updateDiscountType.valid_to = formatDateForApi(updateDiscountType.valid_to)
      }

      if (updateDiscountType.item_date_from) {
        updateDiscountType.item_date_from = formatDateForApi(updateDiscountType.item_date_from)
      }

      if (updateDiscountType.item_date_to) {
        updateDiscountType.item_date_to = formatDateForApi(updateDiscountType.item_date_to)
      }

      return api.updateDiscountType({
        discountType: discountTypeId,
        updateDiscountType,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['discountTypes'])
      },
    }
  )
}

export const useOrderedDiscountTypes = (
  params: any = {},
  options: UseQueryOptions = {}
): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()
  params.siteId = params.siteId ?? siteId

  return useQuery(
    ['discountTypes', { params }],
    async () => await api.listOrderedDiscountTypes({ ...params }),
    options
  )
}

export const useUpdateOrderedDiscountTypes = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ListOrderedDiscountTypes, ResponseError, string[]>(
    (discountTypeIds) =>
      api.updateDiscountTypeOrder({
        updateDiscountTypeOrder: {
          discount_type_ids: discountTypeIds,
          site_id: siteId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['discountTypes'])
      },
    }
  )
}

export const useArchiveDiscountType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (discountTypeId: string) =>
      await api.deleteDiscountType({ discountType: discountTypeId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('discountTypes')
      },
    }
  )
}

export const useRestoreDiscountType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (discountTypeId: string) => api.restoreDiscountType({ discountType: discountTypeId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('discountTypes')
      },
    }
  )
}
